import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as DateCalendar } from 'react-date-range';
import { es } from 'date-fns/locale';
import { CalendarBlank } from '@phosphor-icons/react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const DEFAULT_POPUP_WIDTH = 320;

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatLocalDateTime(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function parseLocalDate(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function parseDateValue(value, mode) {
  if (!value) {
    return null;
  }

  if (mode === 'date') {
    return parseLocalDate(value);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function formatDisplayValue(value, placeholder, mode) {
  const date = parseDateValue(value, mode);
  if (!date) {
    return placeholder;
  }

  if (mode === 'date') {
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

function DateTimePickerField({
  value,
  onChange,
  disabled = false,
  minDate = null,
  mode = 'datetime',
  placeholder = 'dd/mm/aaaa --:--',
  popupWidth = DEFAULT_POPUP_WIDTH,
  buttonClassName = ''
}) {
  const rootRef = useRef(null);
  const popupRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    width: popupWidth
  });

  const parsedDate = parseDateValue(value, mode);
  const [selectedDate, setSelectedDate] = useState(parsedDate || new Date());
  const [selectedTime, setSelectedTime] = useState(
    parsedDate
      ? `${String(parsedDate.getHours()).padStart(2, '0')}:${String(parsedDate.getMinutes()).padStart(2, '0')}`
      : '00:00'
  );

  useEffect(() => {
    const nextDate = parseDateValue(value, mode);
    if (!nextDate) {
      return;
    }

    setSelectedDate(nextDate);
    setSelectedTime(`${String(nextDate.getHours()).padStart(2, '0')}:${String(nextDate.getMinutes()).padStart(2, '0')}`);
  }, [value, mode]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const clickedTrigger = rootRef.current?.contains(event.target);
      const clickedPopup = popupRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPopup) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !rootRef.current || !popupRef.current) {
      return undefined;
    }

    const updatePlacement = () => {
      if (!rootRef.current || !popupRef.current) {
        return;
      }

      const rootRect = rootRef.current.getBoundingClientRect();
      const popupHeight = popupRef.current.offsetHeight || 0;
      const viewportHeight = window.innerHeight || 0;
      const viewportWidth = window.innerWidth || 0;
      const margin = 8;
      const resolvedWidth = Math.min(popupWidth, Math.max(viewportWidth - margin * 2, 0));

      let left = rootRect.left;
      if (left + resolvedWidth > viewportWidth - margin) {
        left = viewportWidth - resolvedWidth - margin;
      }
      if (left < margin) {
        left = margin;
      }

      let top = rootRect.bottom + margin;
      const canOpenAbove = rootRect.top >= popupHeight + margin;
      if (top + popupHeight > viewportHeight - margin && canOpenAbove) {
        top = rootRect.top - popupHeight - margin;
      } else if (top + popupHeight > viewportHeight - margin) {
        top = Math.max(margin, viewportHeight - popupHeight - margin);
      }

      setPopupPosition({
        top,
        left,
        width: resolvedWidth
      });
    };

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen, popupWidth]);

  const commitChange = (date, time) => {
    if (mode === 'date') {
      onChange(formatLocalDate(date));
      return;
    }

    const [hours, minutes] = time.split(':');
    const nextDate = new Date(date);
    nextDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    onChange(formatLocalDateTime(nextDate));
  };

  const handleApply = () => {
    if (mode === 'date') {
      commitChange(selectedDate, selectedTime);
    }

    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        className={`w-full rounded-lg border border-line bg-surface px-3 py-2 text-left text-sm text-content outline-none transition-colors focus:border-amber disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-between ${buttonClassName}`}
      >
        <span>{formatDisplayValue(value, placeholder, mode)}</span>
        <CalendarBlank size={18} className="text-content-muted" />
      </button>

      {isOpen && !disabled && createPortal(
        <div
          ref={popupRef}
          className="fixed z-[20010] rounded-lg border border-line bg-surface-2 p-3 shadow-xl"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            width: `${popupPosition.width}px`,
            maxWidth: '95vw'
          }}
        >
          <DateCalendar
            date={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              commitChange(date, selectedTime);
            }}
            minDate={minDate || undefined}
            locale={es}
            color="#f5b731"
            className="generic-date-range__picker compact-date-picker"
          />

          {mode === 'datetime' ? (
            <div className="mt-2 flex gap-2 border-t border-line pt-2">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-content-muted">Hora</label>
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(event) => {
                    const timeValue = event.target.value;
                    setSelectedTime(timeValue);
                    commitChange(selectedDate, timeValue);
                  }}
                  className="w-full rounded border border-line bg-surface px-2 py-1 text-xs text-content focus:outline-none focus:ring-2 focus:ring-amber"
                  style={{ colorScheme: 'dark' }}
                />
              </div>

              <button
                type="button"
                onClick={handleApply}
                className="self-end rounded bg-amber px-3 py-1 text-xs font-medium text-on-amber hover:bg-amber-hover"
              >
                Aplicar
              </button>
            </div>
          ) : (
            <div className="mt-2 flex justify-end border-t border-line pt-2">
              <button
                type="button"
                onClick={handleApply}
                className="rounded bg-amber px-3 py-1 text-xs font-medium text-on-amber hover:bg-amber-hover"
              >
                Aplicar
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default DateTimePickerField;
